import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, FloppyDisk, CircleNotch, Key, Warning, ArrowsClockwise, Copy, Buildings, Hash, Storefront, Globe } from "@phosphor-icons/react";
import AccountSecuritySection from "@/components/AccountSecuritySection";

function L({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-foreground">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function Card({ icon: Icon, title, subtitle, children, className = "", testId, tone = "default" }) {
  const toneClass = tone === "muted"
    ? "bg-muted/30"
    : tone === "warn"
    ? "bg-warning/5 border-warning/30"
    : "bg-card";
  return (
    <section className={`rounded-xl border border-border ${toneClass} p-6 ${className}`} data-testid={testId}>
      <div className="flex items-center gap-2">
        {Icon && <Icon size={18} weight="duotone" className="text-primary" />}
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
      </div>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function SettingsPage() {
  const { refreshTenant } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/tenants/me").then((r) => {
      setTenant(r.data);
      setForm({
        name: r.data.name,
        brand_name: r.data.brand_name,
        primary_color: r.data.primary_color,
        accent_color: r.data.accent_color,
        contact_email: r.data.contact_email || "",
        contact_phone: r.data.contact_phone || "",
        timezone: r.data.timezone,
        logo_url: r.data.logo_url || "",
        reservation_code_prefix: r.data.reservation_code_prefix || "LOD",
      });
    });
  }, []);

  const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  const submit = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const payload = { ...form };
      if (payload.reservation_code_prefix) {
        payload.reservation_code_prefix = payload.reservation_code_prefix
          .toUpperCase()
          .replace(/[^A-Z0-9-]/g, "")
          .slice(0, 8) || "LOD";
      }
      const { data } = await api.put("/tenants/me", payload);
      setTenant(data);
      setSaved(true);
      await refreshTenant();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(formatApiError(e)); } finally { setSaving(false); }
  };

  if (!tenant) {
    return <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>;
  }

  const previewCode = `${(form.reservation_code_prefix || "LOD").toUpperCase()}-${new Date().getFullYear()}-00001`;

  return (
    <div className="rw-page" data-testid="settings-page">
      {/* Bento grid — most-used settings first */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-6">

        {/* Identity — wide top card */}
        <Card
          icon={Buildings}
          title="Identitet"
          subtitle="Osnovne informacije i branding koje se prikazuju po cijelom sustavu."
          className="lg:col-span-4"
          testId="settings-identity"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <L label="Naziv tenanta">
              <input className="rw-input" value={form.name || ""} onChange={(e) => update("name", e.target.value)} data-testid="tenant-name" />
            </L>
            <L label="Brand naziv (prikazan u UI)">
              <input className="rw-input" value={form.brand_name || ""} onChange={(e) => update("brand_name", e.target.value)} data-testid="tenant-brand" />
            </L>
            <L label="Email">
              <input type="email" className="rw-input" value={form.contact_email || ""} onChange={(e) => update("contact_email", e.target.value)} />
            </L>
            <L label="Telefon">
              <input className="rw-input" value={form.contact_phone || ""} onChange={(e) => update("contact_phone", e.target.value)} />
            </L>
            <L label="URL loga">
              <input className="rw-input" value={form.logo_url || ""} onChange={(e) => update("logo_url", e.target.value)} placeholder="https://..." />
            </L>
            <L label="Vremenska zona">
              <input className="rw-input" value={form.timezone || ""} onChange={(e) => update("timezone", e.target.value)} />
            </L>
          </div>
        </Card>

        {/* Locale + currency + code prefix — narrow side card */}
        <Card
          icon={Globe}
          title="Regionalno"
          subtitle="Format koda rezervacija."
          className="lg:col-span-2"
          testId="settings-regional"
        >
          <div className="space-y-4">
            <L label="Prefix koda rezervacije" hint={<>Primjer: <code className="mono">{previewCode}</code></>}>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground"><Hash size={14} weight="bold" /></span>
                <input
                  className="rw-input mono uppercase"
                  value={form.reservation_code_prefix || ""}
                  onChange={(e) => update("reservation_code_prefix", e.target.value)}
                  maxLength={8}
                  placeholder="LOD"
                  data-testid="reservation-prefix-input"
                />
              </div>
            </L>
          </div>
        </Card>

        {/* Booking Engine API key — wide */}
        <div className="lg:col-span-4">
          <BookingEngineApiKeySection tenant={tenant} onChange={setTenant} />
        </div>

        {/* Channel Manager status — narrow */}
        <Card
          icon={Key}
          title="Kanali (OTA)"
          subtitle="Status povezanosti s vanjskim rezervacijskim platformama."
          className="lg:col-span-2"
          testId="settings-channels"
        >
          {tenant.channex_powered_by_lodgy ? (
            <div className="flex items-start gap-3 rounded-md border border-primary/30 bg-primary/5 p-4" data-testid="channel-wl-banner">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <CheckCircle size={18} weight="fill" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Aktivno</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  <b>Powered by Lodgy</b> — automatski upravljamo vezama s Booking.com, Airbnb i Expedijom. Nema potrebe za trećim računom.
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-warning/30 bg-warning/5 p-4">
              <div className="flex items-start gap-2">
                <Warning size={16} weight="duotone" className="mt-0.5 shrink-0 text-warning" />
                <p className="text-xs text-foreground">
                  Kanal manager nije aktivan. Kontaktiraj Lodgy podršku za aktivaciju.
                </p>
              </div>
            </div>
          )}
          <ChannexDirectKeySection tenant={tenant} onChange={setTenant} />
        </Card>

        {/* Account security — LAST */}
        <div className="lg:col-span-6">
          <AccountSecuritySection />
        </div>
      </div>

      {/* Save bar */}
      <div className="sticky bottom-4 z-10 mt-4 flex items-center justify-between rounded-lg border border-border bg-card p-3 shadow-lg">
        <div className="min-w-0 flex-1">
          {error && <p className="text-xs text-destructive sm:text-sm">{error}</p>}
          {saved && <p className="flex items-center gap-1 text-xs text-success sm:text-sm"><CheckCircle size={14} weight="fill" /> Spremljeno</p>}
        </div>
        <button
          onClick={submit}
          disabled={saving}
          className="rw-btn-primary rw-btn-sm shrink-0"
          data-testid="settings-save"
        >
          <FloppyDisk size={14} weight="duotone" />
          {saving ? "Spremam..." : "Spremi"}
        </button>
      </div>

      <style>{`
        .rw-input { width:100%; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.5rem 0.625rem; font-size:0.875rem; border-radius:0.375rem; color:hsl(var(--foreground)); }
        .rw-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary)/0.2); }
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
      `}</style>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Booking Engine API key section — visually converted to the bento Card style
// but keeps the same UX (rotate + reveal + confirm modal).
// -----------------------------------------------------------------------------
function BookingEngineApiKeySection({ tenant, onChange }) {
  const [revealed, setRevealed] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hasKey = tenant?.public_api_key_set;

  const rotate = async () => {
    setRotating(true); setError(""); setCopied(false);
    try {
      const { data } = await api.post("/tenants/me/rotate-api-key");
      setRevealed(data.public_api_key);
      const fresh = await api.get("/tenants/me");
      onChange(fresh.data);
      setConfirmOpen(false);
    } catch (e) { setError(formatApiError(e)); }
    finally { setRotating(false); }
  };

  const copyKey = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { setError("Kopiranje nije uspjelo — kopiraj ručno."); }
  };

  return (
    <Card
      icon={Storefront}
      title="API ključ — Vanjski Booking Engine"
      subtitle={<>Koristi vanjski Booking Engine za autentikaciju kad šalje rezervacije natrag u PMS. Šalje se u zaglavlju <code className="mono">X-Tenant-API-Key</code>.</>}
      testId="be-api-key-section"
    >
      <div className="rounded-md border border-border bg-muted/30 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Trenutni ključ</p>
            <p className="mt-1 truncate font-mono text-sm text-foreground" data-testid="be-api-key-display">
              {hasKey ? (tenant.public_api_key || "•••• ••••") : "Nije generiran"}
            </p>
            {hasKey && <p className="mt-1 text-[11px] text-muted-foreground">Aktivan — vidi se samo kratki preview iz sigurnosnih razloga.</p>}
          </div>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={rotating}
            className="flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
            data-testid="be-api-key-rotate-btn"
          >
            {rotating ? <CircleNotch size={12} className="animate-spin" /> : <ArrowsClockwise size={12} weight="bold" />}
            {hasKey ? "Generiraj novi" : "Generiraj ključ"}
          </button>
        </div>

        {revealed && (
          <div className="mt-4 rounded-md border-2 border-primary/40 bg-primary/5 p-3" data-testid="be-api-key-reveal">
            <div className="flex items-start gap-2">
              <Warning size={16} weight="duotone" className="mt-0.5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-foreground">Spremi ovaj ključ odmah!</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">Iz sigurnosnih razloga vidiš ga samo SADA. Kopiraj ga u Booking Engine <code className="mono">.env</code> kao <code className="mono">PMS_TENANT_API_KEY</code>.</p>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <code className="flex-1 break-all rounded border border-border bg-background px-3 py-2 font-mono text-xs text-foreground" data-testid="be-api-key-value">{revealed}</code>
              <button onClick={copyKey} className="flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground hover:opacity-90" data-testid="be-api-key-copy">
                {copied ? <><CheckCircle size={12} weight="fill" /> Kopirano</> : <><Copy size={12} /> Kopiraj</>}
              </button>
            </div>
          </div>
        )}

        {error && <div className="mt-3 flex items-center gap-1.5 text-xs text-destructive"><Warning size={12} weight="duotone" /> {error}</div>}

        <div className="mt-4 space-y-1 border-t border-border pt-3 text-[11px] text-muted-foreground">
          <p><strong className="text-foreground">Base URL:</strong> <code className="mono">{window.location.origin}</code></p>
          <p><strong className="text-foreground">Tenant slug:</strong> <code className="mono">{tenant?.slug}</code></p>
          <p className="mt-2">Endpoint: <code className="mono">POST /api/integration/{tenant?.slug}/reservations</code></p>
        </div>
      </div>

      {confirmOpen && (
        <div className="rw-modal-overlay">
          <div className="rw-modal-panel sm:w-[min(440px,96vw)]">
            <div className="rw-modal-body">
              <div className="flex items-start gap-3">
                <Warning size={22} weight="duotone" className="shrink-0 text-warning" />
                <div>
                  <h4 className="rw-h2">{hasKey ? "Generirati novi ključ?" : "Generirati prvi ključ?"}</h4>
                  <p className="mt-1 rw-sublabel">
                    {hasKey
                      ? "Stari ključ će biti odmah deaktiviran. Booking Engine koji ga koristi prestat će raditi dok ne ažuriraš njegov .env."
                      : "Generira se novi tajni ključ koji ćeš koristiti u Booking Engine .env-u."}
                  </p>
                </div>
              </div>
            </div>
            <div className="rw-modal-footer">
              <button onClick={() => setConfirmOpen(false)} className="rw-btn-secondary rw-btn-sm">Odustani</button>
              <button onClick={rotate} disabled={rotating} className="rw-btn-primary rw-btn-sm" data-testid="be-api-key-confirm">
                {rotating && <CircleNotch size={12} className="animate-spin" />}
                {hasKey ? "Generiraj novi" : "Generiraj"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}


// -----------------------------------------------------------------------------
// Direct Channex API key (BYO) — optional override for operators who want to
// bring their own Channex account (e.g. staging testing or power users who are
// not on the Lodgy-managed white-label plan).
// -----------------------------------------------------------------------------
function ChannexDirectKeySection({ tenant, onChange }) {
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [status, setStatus] = useState(null); // {ok, msg}
  const [envMeta, setEnvMeta] = useState(null); // { base_url, property_count }
  const hasKey = Boolean(tenant?.channex_api_key); // masked value from server

  const save = async () => {
    if (!key.trim()) return;
    setSaving(true); setStatus(null);
    try {
      await api.put("/tenants/me", { channex_api_key: key.trim() });
      setStatus({ ok: true, msg: "Ključ spremljen — klikni 'Verificiraj' za provjeru." });
      setKey("");
      // Reload tenant so masked key appears.
      const r = await api.get("/tenants/me");
      onChange(r.data);
    } catch (e) {
      setStatus({ ok: false, msg: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    setVerifying(true); setStatus(null);
    try {
      const { data } = await api.post("/channels/channex/test");
      setEnvMeta({ base_url: data.base_url, property_count: data.channex_property_count });
      setStatus({ ok: true, msg: `Spojen na ${data.base_url} — ${data.channex_property_count} property-a.` });
    } catch (e) {
      setStatus({ ok: false, msg: formatApiError(e) });
    } finally {
      setVerifying(false);
    }
  };

  const clear = async () => {
    if (!confirm("Obrisati trenutni OTA API ključ s ovog računa?")) return;
    setSaving(true);
    try {
      await api.put("/tenants/me", { channex_api_key: "" });
      setStatus({ ok: true, msg: "Ključ obrisan." });
      const r = await api.get("/tenants/me");
      onChange(r.data);
      setEnvMeta(null);
    } catch (e) {
      setStatus({ ok: false, msg: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const registerWebhook = async () => {
    setRegistering(true); setStatus(null);
    try {
      const { data } = await api.post("/channels/channex/register-webhook");
      const n = (data.registered || []).length;
      const errs = (data.errors || []).length;
      const errText = errs ? ` · ${errs} greška${errs === 1 ? "" : "ka"}: ${data.errors.map(e => e.property + " – " + e.error).join("; ")}` : "";
      setStatus({
        ok: !errs,
        msg: `Registrirano ${n} webhook${n === 1 ? "" : "a"} za: ${data.callback_url}${errText}`,
      });
    } catch (e) {
      setStatus({ ok: false, msg: formatApiError(e) });
    } finally {
      setRegistering(false);
    }
  };

  return (
    <div className="mt-4 rounded-md border border-dashed border-border bg-muted/20 p-3" data-testid="settings-channex-byo">
      <div className="flex items-center gap-2">
        <Key size={14} weight="duotone" className="text-primary" />
        <p className="text-xs font-semibold text-foreground">Vlastiti OTA API ključ (napredno)</p>
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
        Zalijepi vlastiti OTA API ključ ako testiraš staging okolinu ili koristiš vlastiti channel manager račun. Prebacuje ovaj račun iz Powered-by-Lodgy u vlastiti (BYO) režim.
      </p>

      {hasKey && (
        <div className="mt-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 p-2.5">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
            <CheckCircle size={12} weight="fill" />
            Trenutni ključ
          </div>
          <div className="mt-1 break-all rounded border border-emerald-500/20 bg-background px-2 py-1 font-mono text-[11px] text-foreground">
            {tenant.channex_api_key || "•••• •••• •••• ••••"}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button type="button" onClick={verify} disabled={verifying}
              className="flex-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-500/20 disabled:opacity-50 dark:text-emerald-300"
              data-testid="settings-channex-verify">
              {verifying ? "Provjeravam…" : "Verificiraj"}
            </button>
            <button type="button" onClick={clear} disabled={saving}
              className="rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-[11px] font-semibold text-destructive hover:bg-destructive/15 disabled:opacity-50"
              data-testid="settings-channex-clear">
              Obriši
            </button>
          </div>
          <button type="button" onClick={registerWebhook} disabled={registering}
            className="mt-2 w-full rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1.5 text-[11px] font-semibold text-primary hover:bg-primary/20 disabled:opacity-50"
            data-testid="settings-channex-register-webhook"
            title="Registrira webhook URL ove instalacije tako da OTA rezervacije stižu automatski">
            {registering ? "Registriram…" : "🔔 Registriraj webhook (za rezervacije)"}
          </button>
        </div>
      )}

      <div className="mt-2 space-y-2">
        <input
          type="password"
          className="rw-input w-full font-mono text-[12px] text-foreground placeholder:text-muted-foreground/50"
          placeholder={hasKey ? "Zalijepi novi ključ (zamijenit će postojeći)…" : "Zalijepi OTA API ključ…"}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          data-testid="settings-channex-key-input"
        />
        <button
          type="button"
          onClick={save}
          disabled={saving || !key.trim()}
          className="w-full rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
          data-testid="settings-channex-save-key"
        >
          {saving ? "Spremam…" : "Spremi novi ključ"}
        </button>
      </div>

      {status && (
        <div className={`mt-2 flex items-start gap-1.5 rounded-md border px-2 py-1.5 text-[11px] ${status.ok ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300" : "border-destructive/30 bg-destructive/5 text-destructive"}`}>
          {status.ok ? <CheckCircle size={11} weight="fill" className="mt-0.5 shrink-0" /> : <Warning size={11} weight="duotone" className="mt-0.5 shrink-0" />}
          <span>{status.msg}</span>
        </div>
      )}
    </div>
  );
}
